import React, { useState } from 'react'
import styled from 'styled-components'
import { Button, theme } from '@lmc/ui-kit'
import { Check, Trash } from '@lmc/ui-kit/src/systemIcons'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from '../../../../../locale'
import { riskAssessmentsConstants } from '../../../../../locale/riskAssessments'
import ArchiveRiskAssessmentModal from './ArchiveRiskAssessmentModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useRoleAccess } from 'components/AccessControl'
import { UserRole } from '@lmc/ts-common'

interface RiskAssessmentEditFooterProps {
	onCancel: () => void
	onSave: () => void
	isEditing: boolean
	isSaveDisabled?: boolean
	isLoading?: boolean
	onArchive?: (reason: string) => void
	archived?: boolean
}

const RiskAssessmentEditFooter: React.FC<RiskAssessmentEditFooterProps> = ({
	onCancel,
	onSave,
	isEditing,
	isSaveDisabled = false,
	isLoading = false,
	archived = false,
}) => {
	const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS)
	const [isArchiveModalVisible, setIsArchiveModalVisible] = useState(false)
	const riskArchivingFlagEnabled = useFeatureFlags({
		flagNames: ['risk_assessments_archiving'],
	})?.data.risk_assessments_archiving.enabled

	const hasRolePermission = useRoleAccess(
		[UserRole.careHomeAdmin],
		[
			{
				i: 'update',
				this: 'RiskAssessment',
			},
		],
	)

	const handleArchiveClick = () => {
		setIsArchiveModalVisible(true)
	}

	const handleArchiveConfirm = (reason: string) => {
		// archive POST request here
		setIsArchiveModalVisible(false)
	}

	const handleArchiveCancel = () => {
		setIsArchiveModalVisible(false)
	}

	return (
		<>
			<FooterContainer>
				<LeftSection>
					{!archived && isEditing && riskArchivingFlagEnabled && (
						<Button variant="default" onClick={handleArchiveClick} disabledByPermission={!hasRolePermission}>
							<Trash />
							{t(riskAssessmentsConstants.ARCHIVE_RISK_ASSESSMENT)}
						</Button>
					)}
				</LeftSection>
				<RightSection>
					<Button
						variant="default"
						onClick={onCancel}
						style={styles.cancelButton}
					>
						{t(riskAssessmentsConstants.CANCEL)}
					</Button>
					<Button
						variant="primary"
						onClick={onSave}
						disabled={isSaveDisabled}
						isLoading={isLoading}
					>
						<Check
							color={
								isSaveDisabled
									? theme.color.neutral.color6
									: theme.color.neutral.color11
							}
						/>
						{t(riskAssessmentsConstants.SAVE)}
					</Button>
				</RightSection>
			</FooterContainer>

			{!archived && (
				<ArchiveRiskAssessmentModal
					isVisible={isArchiveModalVisible}
					onClose={handleArchiveCancel}
					onConfirm={handleArchiveConfirm}
				/>
			)}
		</>
	)
}

const FooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
`

const LeftSection = styled.div`
  display: flex;
`

const RightSection = styled.div`
  display: flex;
`

const styles = {
	cancelButton: {
		marginRight: 10,
	},
}

export default RiskAssessmentEditFooter
